import { useEffect, useMemo, useState } from "react";
import { type RenderConfigPhotoClip } from "@memento/types/render-config";
import { getPluralEnding } from "@memento/library/text";
import { SegmentedToggle } from "src/components/Renderer/SegmentedToggle";
import { useEditor } from "src/components/Renderer/RendererProvider";
import {
  ClipDuration,
  defaultPhotoClipDurationSeconds,
  photoClipDurations,
} from "src/types/renderer";
import { Button } from "@memento/ui/Button";
import { toastError, toastSuccess } from "src/libs/toast-lib";
import { Card } from "@memento/types/card";
import { isPhotoSegment, Segment } from "@memento/types/segment";

// only exported for testing
/** Show the "set all to default duration" button if the duration is not set to the default on all photo clips */
export function getShouldShowDefaultDurationOptionForClip({
  activeSegments,
  defaultPhotoClipDurationSeconds,
}: {
  activeSegments: Segment[];
  defaultPhotoClipDurationSeconds: number;
}) {
  return activeSegments.some(
    (segment) =>
      isPhotoSegment(segment) &&
      segment.durationSeconds !== defaultPhotoClipDurationSeconds
  );
}

type EditPhotoClipDurationProps = {
  clip: RenderConfigPhotoClip;
  card: Card;
};

export const EditPhotoClipDuration = ({
  clip,
  card,
}: EditPhotoClipDurationProps) => {
  const [updateFrameToSeekOnNextRender, setUpdateFrameToSeekOnNextRender] =
    useState(false);

  const {
    setClipDuration,
    setAllPhotoClipsToDefaultDuration,
    allSegments,
    setFrameToSeek,
    clipPrefetchDetailsBySegmentId,
  } = useEditor();

  function handleSetDuration(segmentId: string, duration: ClipDuration) {
    setClipDuration(segmentId, duration);
  }

  async function handleSetAllDuration() {
    try {
      await setAllPhotoClipsToDefaultDuration(clip.durationSeconds);

      // The skip to frame for this clip may have changed but the skip to frame
      // will not have updated yet. We need to wait for the next render to set
      // it.
      setUpdateFrameToSeekOnNextRender(true);
    } catch (_err) {
      toastError(
        "Something went wrong and the duration could not be saved. Please try again."
      );
    }
  }

  useEffect(() => {
    if (updateFrameToSeekOnNextRender) {
      setUpdateFrameToSeekOnNextRender(false);

      const skipToFrame =
        clipPrefetchDetailsBySegmentId[clip.segmentId]?.skipToFrame ?? 0;

      setFrameToSeek({ frame: skipToFrame, pause: true });

      toastSuccess(
        `All photo clips set to ${clip.durationSeconds} second${getPluralEnding(
          clip.durationSeconds
        )}.`
      );
    }
  }, [
    updateFrameToSeekOnNextRender,
    clipPrefetchDetailsBySegmentId,
    clip.segmentId,
    setFrameToSeek,
    clip.durationSeconds,
  ]);

  const activeSegments = useMemo(() => {
    return allSegments.filter((segment) => !segment.isDeleted);
  }, [allSegments]);

  const shouldShowDefaultDurationOptionForClip = useMemo(
    () =>
      getShouldShowDefaultDurationOptionForClip({
        activeSegments,
        defaultPhotoClipDurationSeconds:
          card.defaultPhotoClipDurationSeconds ??
          defaultPhotoClipDurationSeconds,
      }),
    [activeSegments, card.defaultPhotoClipDurationSeconds]
  );

  return (
    <div className="flex flex-col gap-3">
      <SegmentedToggle
        options={photoClipDurations.map((duration) => ({
          name: `${duration} seconds`,
          value: duration,
        }))}
        className="px-5 md:px-1 lg:px-2 xl:px-5"
        selectedValue={clip.durationSeconds as ClipDuration}
        onChange={(value) => handleSetDuration(clip.segmentId, value)}
      />
      {shouldShowDefaultDurationOptionForClip ? (
        <Button
          variant="primary"
          onClick={handleSetAllDuration}
          disabled={!shouldShowDefaultDurationOptionForClip}
        >
          Set all photo clips to {clip.durationSeconds} second
          {getPluralEnding(clip.durationSeconds)}
        </Button>
      ) : null}
    </div>
  );
};
